/**
 * Clipboard-link suggestion rules for the Add Recipe sheet (issue #532).
 *
 * Kept separate from the component so the rules that caused the two reported
 * bugs are directly testable:
 *   1. the sheet must never read the clipboard by itself on the web — every
 *      browser answers a programmatic read with its own permission prompt,
 *      which is what appeared instead of the add-a-recipe sheet;
 *   2. the clipboard still holds the link you just imported, so offering it
 *      again traps you on the recipe you already added.
 */

/** The slice of `navigator.permissions` the check below needs. */
export interface ClipboardPermissions {
  query: (descriptor: { name: string }) => Promise<{ state: string }>;
}

/**
 * Can the clipboard be read without the platform prompting the user first?
 *
 * Native: always. Web: only where the cook has already allowed it — Chrome and
 * Edge keep a `clipboard-read` permission, granted by the first deliberate
 * paste. Safari shows a "Paste" callout on every read and Firefox refuses
 * outright; neither knows the permission name, so `query` rejects and the
 * answer is no. A `prompt` state is a no as well: reading there is exactly
 * what puts the browser's dialog in front of the sheet.
 */
export const canPeekClipboardSilently = async (
  platformOS: string,
  permissions: ClipboardPermissions | undefined =
    typeof navigator === 'undefined' ? undefined : (navigator.permissions as ClipboardPermissions | undefined),
): Promise<boolean> => {
  if (platformOS !== 'web') return true;
  if (!permissions?.query) return false;
  try {
    const status = await permissions.query({ name: 'clipboard-read' });
    return status.state === 'granted';
  } catch {
    return false;
  }
};

/** http(s) URL check — the only thing we can import from a link. */
export const isImportableUrl = (text: string | null | undefined): boolean => {
  if (!text) return false;
  try {
    const url = new URL(text.trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Should we offer this clipboard content as a link suggestion?
 *
 * Returns false for the link that was just imported: the clipboard is unchanged
 * after an import, and re-offering it is what made adding a second recipe
 * impossible.
 */
export const shouldOfferClipboardLink = (
  clipboardText: string | null | undefined,
  lastImportedUrl: string | null,
): boolean => {
  if (!isImportableUrl(clipboardText)) return false;
  return clipboardText!.trim() !== lastImportedUrl;
};
